import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { AccountInvitationConfirmation } from "./AccountInvitationConfirmation";
import { IPortalManagementPopupErrorFunction } from "./LinkMylePatientToPortalAccountDialog";
import { PortalActionConfirmationGenericPopup } from "./PortalActionConfirmationGenericPopup";
import { RelinkPortalAccountPopup } from "./RelinkPortalAccountPopup";
import { ResendPortalEmailConfirmationPopup } from "./ResendPortalEmailConfirmationPopup";

import style from "./AccountDetails.module.scss";

interface IAccountDetailsProps {
    overview: GetPatientOverviewResponse;
    patientContactId: string;
    patientInfo: Patient;

    areControlsDisabled?: boolean;
    isEmailChangeDisabled?: boolean;
    isStatusPopup?: boolean;
}

class AccountDetailsState {
    public isLoading: boolean;
    public errors: string[];
    public isDeleteAccountPopupVisible: boolean;
    public isDeactivationPopupVisible: boolean;
    public isReactivationPopupVisible: boolean;
    public isResendEmailPopupVisible: boolean;
    public isRelinkAccountPopupVisible: boolean;
    public resendEmailPopupTitle: string;
    public resendEmailPopupMessage: JSX.Element | string;
    public resendEmailConfirmationCallback: (onError: IPortalManagementPopupErrorFunction) => void;
    public emailChangeText: string = "";
    public isEmailValid: boolean = false;
    public isChangingEmail: boolean = false;
    public isChangeEmailDisabled: boolean = false;
    public emailValidationRule: ValidationRule;
    public emailMask: string;
    public emailValidationRegex: string;
    public emailValidationErrorMessage: string;

    public displayConfirmationCode: boolean = false;
    public confirmationCode: string;
    public clinicalCode: string;

    constructor(s: AccountDetailsState = null) {
        if (s != null) {
            this.isLoading = s.isLoading;
            this.errors = s.errors;
            this.isDeleteAccountPopupVisible = s.isDeleteAccountPopupVisible;
            this.isDeactivationPopupVisible = s.isDeactivationPopupVisible;
            this.isReactivationPopupVisible = s.isReactivationPopupVisible;
            this.isResendEmailPopupVisible = s.isResendEmailPopupVisible;
            this.isRelinkAccountPopupVisible = s.isRelinkAccountPopupVisible;
            this.resendEmailPopupTitle = s.resendEmailPopupTitle;
            this.resendEmailPopupMessage = s.resendEmailPopupMessage;
            this.resendEmailConfirmationCallback = s.resendEmailConfirmationCallback;

            this.emailChangeText = s.emailChangeText;
            this.isEmailValid = s.isEmailValid;
            this.isChangingEmail = s.isChangingEmail;
            this.isChangeEmailDisabled = s.isChangeEmailDisabled;
            this.emailValidationRule = s.emailValidationRule;
            this.emailMask = s.emailMask;
            this.emailValidationRegex = s.emailValidationRegex;
            this.emailValidationErrorMessage = s.emailValidationErrorMessage;
            this.displayConfirmationCode = s.displayConfirmationCode;
            this.confirmationCode = s.confirmationCode;
            this.clinicalCode = s.clinicalCode;
        }
    }
}

class AccountDetails extends React.Component<IAccountDetailsProps, AccountDetailsState> {
    private validationHelper = new ValidationHelper();

    constructor(props: IAccountDetailsProps) {
        super(props);

        const initialState = new AccountDetailsState();
        initialState.resendEmailPopupTitle = "";
        initialState.resendEmailPopupMessage = "";
        this.state = initialState;
    }

    public componentDidMount(): void {
        const emailValidationRule = this.validationHelper.getEmailValidationRule();

        this.setState({
            emailValidationRule,
            emailMask: emailValidationRule?.EditMaskPattern ?? "",
            emailValidationRegex: emailValidationRule?.ValidationRegexPattern ?? "",
            emailValidationErrorMessage: emailValidationRule?.ValidationErrorMessageLookupId
                ? cacheStore.GetLocalizedValueByLookupId(emailValidationRule.ValidationErrorMessageLookupId)
                : "",
            resendEmailConfirmationCallback: this.onResendEmail.bind(this)
        });
    }

    public resendEmailLinkMessage(authCode: string): JSX.Element {
        if (authCode === undefined || authCode.length === 0) {
            return (
                <div className="content">
                    <h4 className="thin-font"> {cacheStore.GetString("SendInvitationSuccessLabel")} :</h4>
                    <p className="bold">{this.props.overview.Account.Email}</p>
                </div>
            );
        }
        return (
            <div className="content">
                <h4 className="thin-font"> {cacheStore.GetString("SendInvitationSuccessLabel")} :</h4>
                <p className="bold">{this.props.overview.Account.Email}</p>
                <p> {cacheStore.GetString("SendInvitationCodeLabel")} : </p>
                <h2>{authCode}</h2>
            </div>
        );
    }

    public render(): JSX.Element {
        let self = this;
        let isAccountLocked = false;
        if (this.props.overview.Account != null && this.props.overview.Account.LastLocked != null && this.props.overview.Account.LockedUntil != null) {
            let lockedUntil = moment.utc(this.props.overview.Account.LockedUntil);
            let now = moment.utc();
            isAccountLocked = now < lockedUntil;
        }

        let isExpired = false;
        if (this.props.overview.Account && !this.props.overview.Account.IsActive && this.props.overview.Account.CreatedOnConfirmationCode)
            isExpired = moment(this.props.overview.Account.CreatedOnConfirmationCode).add(cacheStore.portalConfiguration?.ActivationCodeExpirationHours, "hours").toDate() < moment().toDate();

        let verticalAlignCodeStyle: React.CSSProperties = { verticalAlign: "middle" };
        let marginTopConfirmationCodeStyle: React.CSSProperties;
        if (this.props.areControlsDisabled) marginTopConfirmationCodeStyle = { marginTop: "7px" };
        let isRelinkButtonDisplayed = true;
        if (this.props.patientInfo && this.props.patientInfo.IndividualResponse && this.props.patientInfo.IndividualResponse.DateOfBirth) {
            if (this.props.patientInfo.getAge() < cacheStore.ClinicSettings.PatientAgeOfConsent)
                isRelinkButtonDisplayed = false;
        }

        const usePortalCommunicationCss = cacheStore.ClinicSettings.EnableBidirectionalCommunication && this.props.isStatusPopup;
        const columnOneLabelCss = usePortalCommunicationCss ? style.accountDetailsLabelColumnC1 : "c1-50p";
        const textContentCss = usePortalCommunicationCss ? style.accountDetailsFontNormal : style.accountDetailsFontBold;

        const result: JSX.Element = (
            <div className="block separated">

                {this.state.isDeleteAccountPopupVisible && (
                    <PortalActionConfirmationGenericPopup
                        confirmationCallback={this.onConfirmDelete.bind(this)}
                        confirmationTitle={cacheStore.GetString("ConfirmDeleteAccountLabel")}
                        confirmationMessage={cacheStore.GetString("AccountDeletionMessageLabel")}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                {this.state.isDeactivationPopupVisible && (
                    <PortalActionConfirmationGenericPopup
                        confirmationCallback={this.onConfirmLock.bind(this)}
                        confirmationTitle={cacheStore.GetString("ConfirmAccountSuspensionLabel")}
                        confirmationMessage={cacheStore.GetString("AccountSuspensionMessageLabel")}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                {this.state.isReactivationPopupVisible && (
                    <PortalActionConfirmationGenericPopup
                        confirmationCallback={this.onConfirmUnlock.bind(this)}
                        confirmationTitle={cacheStore.GetString("AccountReactivationMessageLabel")}
                        confirmationMessage={cacheStore.GetString("AccountReactivationMessage")}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                {this.state.isResendEmailPopupVisible && (
                    <ResendPortalEmailConfirmationPopup
                        confirmationCallback={this.state.resendEmailConfirmationCallback}
                        confirmationTitle={this.state.resendEmailPopupTitle}
                        confirmationMessage={this.state.resendEmailPopupMessage}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                {this.state.isRelinkAccountPopupVisible && (
                    <RelinkPortalAccountPopup
                        patientInfo={this.props.patientInfo}
                        patientContactId={self.props.patientContactId}
                        componentId="patientPortal_sub_linkMemberAccount"
                        onCloseCallback={confirmationCode => self.onClosePopups(confirmationCode)}
                    />
                )}

                <div className="flex-container heading">
                    <h3 className="h thin-font">{cacheStore.GetString("AccountLabel")}: {cacheStore.GetString("DetailsLabel")} </h3>
                    {!this.props.areControlsDisabled && (
                        <div>
                            {this.props.overview.Account && (
                                <button onClick={this.displayDeleteAccountDialog.bind(this)} className="btn btn-danger push-right-5">
                                    <span>{cacheStore.GetString("DeleteAccountLabel")}</span>
                                </button>
                            )}
                            {this.props.overview.Account && this.props.overview.Account.IsActive && isAccountLocked && (
                                <button onClick={this.displayReactivateAccountDialog.bind(this)} className="btn btn-danger push-right-5">
                                    <span>{cacheStore.GetString("UnlockAccountLabel")}</span>
                                </button>
                            )}
                            {this.props.overview.Account && this.props.overview.Account.IsActive && !isAccountLocked && (
                                <button onClick={this.displayDeactivateAccountDialog.bind(this)} className="btn btn-danger push-right-5">
                                    <span>{cacheStore.GetString("LockAccountLabel")}</span>
                                </button>
                            )}
                            {this.props.overview.Account && this.props.overview.Account.IsActive && (
                                <button disabled={isAccountLocked} onClick={this.displayResetPasswordDialog.bind(this)} className="btn btn-primary push-right-5">
                                    <span>{cacheStore.GetString("ResetPortalPasswordLabel")}</span>
                                </button>
                            )}
                            {this.props.overview.Account && !this.props.overview.Account.IsActive && (
                                <button disabled={isAccountLocked} onClick={this.displaySendActivationEmailDialog.bind(this)} className="btn btn-primary push-right-5">
                                    <span>{cacheStore.GetString("SendPortalActivationLabel")}</span>
                                </button>
                            )}
                            {isRelinkButtonDisplayed && (
                                <button onClick={this.displayRelinkAccountDialog.bind(this)} className="btn btn-primary push-right-5">
                                    <span>{cacheStore.GetString("RelinkLabel")}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex-container center">
                    <div className={`column ${usePortalCommunicationCss ? "" : "centered"}`}>

                        {this.props.overview.Account == null &&
                            <div> </div>
                        }

                        {this.props.overview.Account != null && (
                            <table>
                                <tbody>
                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("EmailLabel")}</label>
                                        </td>
                                        <td className="c2 left-align flex-container">
                                            {(this.state.isChangingEmail == false && this.state.displayConfirmationCode == false) && (
                                                <span className="flex-container">
                                                    <span className={`"push-right-5" ${textContentCss}`}>
                                                        {this.props.overview.Account.Email}
                                                    </span>

                                                    {!this.props.areControlsDisabled && !this.props.isEmailChangeDisabled && (
                                                        <button disabled={isAccountLocked} onClick={this.changeEmailClick.bind(this)} className="btn btn-primary flex-end">
                                                            <span>{cacheStore.GetString("ChangeEmailLabel")}</span>
                                                        </button>
                                                    )}
                                                </span>
                                            )}

                                            {this.state.displayConfirmationCode && (
                                                <span>
                                                    <div>
                                                        <AccountInvitationConfirmation registrationCode={this.state.confirmationCode} email={this.props.overview.Account.Email} />
                                                    </div>
                                                    <div>
                                                        <button onClick={this.closeConfirmationDialog.bind(this)} className="btn  push-right-5">
                                                            <span>{cacheStore.GetString("OKLabel")}</span>
                                                        </button>
                                                    </div>
                                                </span>
                                            )}

                                            {this.state.isChangingEmail && (
                                                <span style={{ width: "100%" }}>
                                                    <TextboxMaskedControl
                                                        isValid={this.state.isEmailValid}
                                                        alphanumericIncludesPeriod={false}
                                                        returnMaskedValue={true}
                                                        id="email_portal"
                                                        value={this.state.emailChangeText}
                                                        onChangeFunction={this.onEmailTextChanged.bind(this)}
                                                        mask={this.state.emailMask}
                                                        validationErrorMessage={this.state.emailValidationErrorMessage}
                                                        isEmail={true}
                                                    />
                                                    {!this.props.areControlsDisabled && (
                                                        <div className="text-right" style={{ marginTop: "5px" }}>
                                                            <button
                                                                onClick={this.changeEmailCancelClick.bind(this)}
                                                                className="btn btn-default push-right-5"
                                                            >
                                                                {cacheStore.GetString("CancelLabel")}
                                                            </button>
                                                            <button
                                                                onClick={this.changeEmailSaveClick.bind(this)}
                                                                className="btn btn-primary push-left-5"
                                                            >
                                                                {cacheStore.GetString("SaveLabel")}
                                                            </button>
                                                        </div>
                                                    )}
                                                </span>
                                            )}

                                        </td>

                                    </tr>
                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("AccountStatusLabel")}</label>
                                        </td>
                                        <td className="c2 left-align">
                                            <span className={textContentCss}>
                                                {this.props.overview.Account.IsActive &&
                                                    <span>{cacheStore.GetString("Active3Label")}</span>
                                                }
                                                {!this.props.overview.Account.IsActive &&
                                                    <span className="red-text">{cacheStore.GetString("Inactive")}</span>
                                                }
                                                {(isAccountLocked || this.props.overview.Account.IsActivationTokenInvalid) &&
                                                    <span className="red-text"> - {cacheStore.GetString("LockedAccountLabel").toUpperCase()}</span>
                                                }
                                            </span>
                                        </td>
                                    </tr>
                                    {(this.props.overview.Account && !this.props.overview.Account.IsActive) && (
                                        <tr>
                                            {cacheStore.ClinicSettings.EnableDateOfBirthPortalActivation
                                                ? (
                                                    <td className={columnOneLabelCss} style={verticalAlignCodeStyle}>
                                                        <label className="form-label">{cacheStore.GetString("PortalRegistrationLabel")}</label>
                                                    </td>
                                                )
                                                :
                                                (
                                                    <td className={columnOneLabelCss} style={verticalAlignCodeStyle}>
                                                        <label className="form-label">{cacheStore.GetString("RegistrationCodeLabel")}</label>
                                                    </td>
                                                )
                                            }
                                            <td className="c2 left-align" style={verticalAlignCodeStyle}>
                                                <span className="flex-container">
                                                    {this.state.clinicalCode && (
                                                        <span className={textContentCss} style={{ fontSize: "x-large" }}>
                                                            {this.state.clinicalCode}
                                                        </span>
                                                    )}
                                                    {(!this.state.clinicalCode && !IsNullOrWhiteSpace(this.props.overview.Account.ConfirmationCode) && !isExpired) && (
                                                        <span className={textContentCss} style={{ fontSize: "x-large" }}>
                                                            {this.props.overview.Account.ConfirmationCode}
                                                        </span>
                                                    )}
                                                    {(!this.state.clinicalCode && !IsNullOrWhiteSpace(this.props.overview.Account.ConfirmationCode) && isExpired) && (
                                                        <span className={`red-text ${textContentCss}`} style={marginTopConfirmationCodeStyle}>
                                                            {cacheStore.GetString("ExpiredLabel2")}
                                                        </span>
                                                    )}
                                                    {(!this.state.clinicalCode && IsNullOrWhiteSpace(this.props.overview.Account.ConfirmationCode)) && (
                                                        <span className={textContentCss} style={marginTopConfirmationCodeStyle}>--</span>
                                                    )}
                                                    {this.props.areControlsDisabled && this.props.overview.Account && this.props.overview.Account.IsActive && (
                                                        <button disabled={isAccountLocked} onClick={() => this.onResendEmail()} className="btn btn-primary flex-end">
                                                            <span>{cacheStore.GetString("ResetPortalPasswordLabel")}</span>
                                                        </button>
                                                    )}
                                                    {this.props.areControlsDisabled && this.props.overview.Account && !this.props.overview.Account.IsActive && (
                                                        <button disabled={isAccountLocked} onClick={() => this.onResendEmail()} className="btn btn-primary flex-end">
                                                            <span>{cacheStore.GetString("SendPortalActivationLabel")}</span>
                                                        </button>
                                                    )}
                                                </span>
                                            </td>
                                        </tr>
                                    )}

                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("LastLoginLabel")}</label>
                                        </td>

                                        {this.props.overview.Account.LastLogin != null && (
                                            <td className="c2 left-align">
                                                <span className={textContentCss}>
                                                    {moment.utc(this.props.overview.Account.LastLogin).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                                </span>
                                            </td>
                                        )}
                                        {this.props.overview.Account.LastLogin == null && (
                                            <td className="c2 left-align">
                                                <span className={textContentCss}>--</span>
                                            </td>
                                        )}
                                    </tr>
                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("LastLoginAttemptLabel")}</label>
                                        </td>
                                        <td className="c2 left-align">
                                            <span className={textContentCss}>
                                                {moment.utc(this.props.overview.Account.LastLoginAttempt).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("ActivationDateLabel")}</label>
                                        </td>

                                        {this.props.overview.Account.DateActivated != null && (
                                            <td className="c2 left-align">
                                                <span className={textContentCss}>
                                                    {moment.utc(this.props.overview.Account.DateActivated).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                                </span>
                                            </td>
                                        )}
                                        {this.props.overview.Account.DateActivated == null && (
                                            <td className="c2 left-align">
                                                <span className={textContentCss}>--</span>
                                            </td>
                                        )}
                                    </tr>
                                    <tr>
                                        <td className={columnOneLabelCss}>
                                            <label className="form-label">{cacheStore.GetString("AccountCreatedLabel")}</label>
                                        </td>
                                        <td className="c2 left-align">
                                            <span className={textContentCss}>
                                                {moment.utc(this.props.overview.Account.DateCreated).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                            </span>
                                        </td>
                                    </tr>
                                    {isAccountLocked && (
                                        <tr>
                                            <td className={columnOneLabelCss}>
                                                <label className="form-label">{cacheStore.GetString("LockedOnLabel")}</label>
                                            </td>
                                            <td className="c2 left-align red-text">
                                                <span className={textContentCss}>
                                                    {moment.utc(this.props.overview.Account.LastLocked).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                    {isAccountLocked && (
                                        <tr>
                                            <td className={columnOneLabelCss}>
                                                <label className="form-label">{cacheStore.GetString("LockedUntilLabel")}</label>
                                            </td>
                                            <td className="c2 left-align red-text">
                                                <span className={textContentCss}>
                                                    {moment.utc(this.props.overview.Account.LockedUntil).local().format("YYYY-MM-DD [@]HH:mm:ss A")}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );

        return result;
    }

    private onClosePopups(confirmationCode: string = null): void {
        let s = new AccountDetailsState();
        if (!IsNullOrWhiteSpace(confirmationCode))
            s.clinicalCode = confirmationCode;
        s.isDeleteAccountPopupVisible = false;
        s.isDeactivationPopupVisible = false;
        s.isReactivationPopupVisible = false;
        s.isResendEmailPopupVisible = false;
        s.isRelinkAccountPopupVisible = false;
        s.resendEmailConfirmationCallback = onError => this.onResendEmail(onError);
        s.resendEmailPopupTitle = "";
        s.resendEmailPopupMessage = "";
        this.setState(s);
    }

    private displayDeleteAccountDialog(): void {
        let s = new AccountDetailsState();
        s.isDeleteAccountPopupVisible = true;
        this.setState(s);
    }

    private displayDeactivateAccountDialog(): void {
        let s = new AccountDetailsState();
        s.isDeactivationPopupVisible = true;
        this.setState(s);
    }

    private displayReactivateAccountDialog(): void {
        let s = new AccountDetailsState();
        s.isReactivationPopupVisible = true;
        this.setState(s);
    }

    private displayResetPasswordDialog(): void {
        let s = new AccountDetailsState();
        s.isResendEmailPopupVisible = true;
        s.resendEmailPopupTitle = cacheStore.GetString("ResetPortalPasswordLabel");
        s.resendEmailPopupMessage = cacheStore.GetString("ResetPortalPasswordPopupLabel");
        this.setState(s);
    }

    private displaySendActivationEmailDialog(): void {
        let s = new AccountDetailsState();
        s.isResendEmailPopupVisible = true;
        s.resendEmailPopupTitle = cacheStore.GetString("SendPortalActivationLabel");
        s.resendEmailPopupMessage = cacheStore.ClinicSettings.EnableDateOfBirthPortalActivation
            ? ""
            : cacheStore.GetString("SendPortalActivationPopupLabel");
        this.setState(s);
    }

    private displayRelinkAccountDialog(): void {
        let s = new AccountDetailsState();
        s.isRelinkAccountPopupVisible = true;
        this.setState(s);
    }

    private onConfirmDelete(onError: IPortalManagementPopupErrorFunction): void {
        let self = this;
        let client = new Client();
        let s = new AccountDetailsState(this.state);
        this.setState(s, () => {
            client.Post(
                ServerOperations.Server.Portal_Proxy_DeleteAccount,
                { PatientId: self.props.patientContactId },
                response => self.onConfirmDeleteAction(response),
                err => onError(err)
            );
        });
    }

    private onConfirmDeleteAction(response: ServerEvent): void {
        let errors = response.Errors as ErrorMessage[];

        if (errors == null || errors.length == 0) {
            // reload portal patient account section
            Dispatcher.dispatch(LocalOperations.Portal.PortalManagment_LoadPortalProfileOverview, { patientId: this.props.patientContactId });
            this.onClosePopups();
        }
    }

    private onConfirmLock(onError: IPortalManagementPopupErrorFunction): void {
        let self = this;
        let client = new Client();
        let s = new AccountDetailsState(this.state);
        this.setState(s, () => {
            client.Post(ServerOperations.Server.Portal_Proxy_LockAccount, { PatientId: self.props.patientContactId }, response => self.onConfirmLockAction(response), err => onError(err));
        });
    }

    private onConfirmUnlock(onError: IPortalManagementPopupErrorFunction): void {
        let self = this;
        let client = new Client();
        let s = new AccountDetailsState(this.state);
        this.setState(s, () => {
            client.Post(ServerOperations.Server.Portal_Proxy_LockAccount, { PatientId: self.props.patientContactId, IsUnlock: true }, response => self.onConfirmLockAction(response), err => onError(err));
        });
    }

    private onConfirmLockAction(response: ServerEvent): void {
        let errors = response.Errors as ErrorMessage[];

        if (errors == null || errors.length == 0) {
            // reload portal patient account section
            const op = new LocalEvent(LocalOperations.Portal.Account_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            LocalEventDispatcher.dispatch(op);
            this.onClosePopups();
        }
    }

    private onResendEmail(onError?: IPortalManagementPopupErrorFunction): void {
        let self = this;
        let client = new Client();

        let s = new AccountDetailsState(self.state);
        s.isLoading = true;

        this.setState(s, () => {
            client.Post(
                ServerOperations.Server.Portal_Proxy_ResendLinkEmail,
                {
                    PatientId: this.props.patientContactId,
                    Email: this.props.overview.Account.Email
                },
                response => {
                    this.onResendEmailSuccess(response);
                },
                err => {
                    console.log(err); onError?.(err);
                }
            );
        });
    }

    private onResendEmailSuccess(response: ServerEvent): void {
        let errors = response.Errors as ErrorMessage[];

        if ((errors == null || errors.length == 0)) {
            let s = new AccountDetailsState(this.state);
            s.clinicalCode = response["AuthCode"];
            if (this.props.areControlsDisabled) {
                s.isLoading = false;
            }
            else {
                s.resendEmailPopupMessage = this.resendEmailLinkMessage(response["AuthCode"]);
                s.resendEmailConfirmationCallback = null;
            }
            this.setState(s);
        }
        else {
            let s = new AccountDetailsState(this.state);

            if (this.props.areControlsDisabled) {
                s.clinicalCode = null;
                s.isLoading = false;
            }
            else {
                s.resendEmailPopupMessage = cacheStore.GetString("ResendEmailPortalResetErrorMessage");
                s.resendEmailConfirmationCallback = null;
            }
            this.setState(s);
        }
    }

    private changeEmailClick(): void {
        var s = new AccountDetailsState();
        s.isChangingEmail = true;
        s.isChangeEmailDisabled = false;
        s.emailChangeText = this.props.overview.Account.Email;
        this.setState(s);
    }

    private onEmailTextChanged(newValue: string): void {
        var s = new AccountDetailsState(this.state);
        s.emailChangeText = newValue;
        var valid = ValidationHelper.isExactMatch(s.emailChangeText, ValidationHelper.emailRegex);
        s.isEmailValid = valid;
        this.setState(s);
    }

    private changeEmailCancelClick(): void {
        var s = new AccountDetailsState();
        s.isChangingEmail = false;
        s.isChangeEmailDisabled = false;
        this.setState(s);
    }

    private changeEmailSaveClick(): void {
        this.onChangeEmail();
        var s = new AccountDetailsState();
        s.isChangingEmail = true;
        s.isChangeEmailDisabled = true;
        this.setState(s);
    }

    private onChangeEmail(): void {
        let self = this;
        let client = new Client();

        // call change email backend
        client.Post(
            ServerOperations.Server.Portal_Proxy_AccountChangeEmail,
            {
                MyleContactId: this.props.patientContactId,
                NewEmail: this.state.emailChangeText
            },
            response => self.onChangeEmailSuccess(response),
            err => self.onError(err)
        );

        var s = new AccountDetailsState(self.state);
        s.isLoading = true;
        self.setState(s);
    }

    private onChangeEmailSuccess(response: any): void {
        let errors = response.Errors as ErrorMessage[];

        if (errors == null || errors.length == 0) {
            // todo reload portal patient account section
            const op = new LocalEvent(LocalOperations.Portal.Account_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            LocalEventDispatcher.dispatch(op);

            var s = new AccountDetailsState();
            s.isChangingEmail = false;
            s.isChangeEmailDisabled = false;
            s.isLoading = false;
            s.displayConfirmationCode = true;
            s.confirmationCode = response.AuthenticationCode;
            s.clinicalCode = response.AuthenticationCode;
            s.errors = [];
            this.setState(s);
        }
    }

    private onError(error: string): void {
        var s = new AccountDetailsState(this.state);
        s.isLoading = false;
        s.errors = [error];
        this.setState(s);
    }

    private closeConfirmationDialog(): void {
        var s = new AccountDetailsState();
        s.displayConfirmationCode = false;
        s.confirmationCode = "";
        s.emailChangeText = "";
        this.setState(s);
    }
}

export { AccountDetails };
